import { useLayoutEffect, useRef, type ComponentProps, type ReactNode } from 'react';
import type { DocumentFont, LengthUnit, NodeRecord } from '@flobi/artwork-core';
import type { Rail } from '../tools/types';
import { presetMenu } from '../artboardPresets';
import { MenuWell } from './MenuWell';
import { PaintControl } from './Paint';
import { PageInspector } from './PageInspector';
import { LayerEffects, PositionSection, SelectionColorsOf, SelectionInspector, type SelectionActions } from './SelectionInspector';
import { type DrawWith } from './ToolOptions';
import { IconButton, Section, SegmentedControl } from '../../ui/controls';
import type { RepeatActions } from './RepeatPanel';

/**
 * The side panel: the one place that changes with what you are doing, and shows nothing
 * else.
 *
 * It is the depth behind the quick bar: object settings lead, followed by Layout,
 * Appearance, Fill, Stroke and Effects. Its header names the current
 * context and keeps Close within reach. Animate is not here: it is a bar along the bottom.
 *
 * It is docked to the trailing edge of the workspace; the canvas fits beside it.
 */

const COARSE = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
export const PANEL_WIDTH = COARSE ? 280 : 241;
/** The side panel's tabs when one picture or clip is chosen; otherwise it is Properties alone. */
export type PanelTab = 'properties' | 'edit' | 'info';

export function SidePanel({
  onClose,
  tool,
  toolLabel,
  rail,
  drawWith,
  nodes,
  canAlign = nodes.length > 1,
  anchor,
  units,
  actions,
  fonts,
  page,
  repeat,
  variationRail,
  blendRail,
  edit,
  info,
  tab = 'properties',
  onTab,
}: {
  onClose: () => void;
  tool: string;
  toolLabel: string;
  rail: Rail | null;
  drawWith: DrawWith;
  nodes: readonly NodeRecord[];
  canAlign?: boolean;
  anchor: { index: number; at: { x: number; y: number } } | null;
  units: LengthUnit;
  actions: SelectionActions;
  fonts: readonly DocumentFont[];
  page: Omit<ComponentProps<typeof PageInspector>, 'artboard'> & { artboard: NodeRecord | null };
  repeat?: RepeatActions;
  variationRail?: Rail | null;
  blendRail?: Rail | null;
  /**
   * The Edit tab — a picture's or clip's tools (imageTools/PicturePanel) — for one chosen; with it
   * the panel has two tabs, Properties and Edit (Tarek, 15 Oct: "keep the same property tab —
   * when I press on Edit, open the edit tab"). The bar's Edit opens it; Properties is unchanged.
   */
  edit?: ReactNode;
  /** The Info tab — what the picture is and, when generated, how it was made (ai/GenerationInfo). */
  info?: ReactNode;
  tab?: PanelTab;
  onTab?: (tab: PanelTab) => void;
}) {
  const scroll = useRef<HTMLDivElement>(null);
  const position = useRef({ context: '', top: 0 });
  const shown: PanelTab = tab === 'edit' && edit ? 'edit' : tab === 'info' && info ? 'info' : 'properties';
  const context = `${tool}:${shown}:${nodes.map(node => node.id).join(',')}`;
  // A document edit must not move the inspector under the pointer. Only a change
  // of selection/tool starts a new scroll context.
  useLayoutEffect(() => {
    if (!scroll.current) return;
    if (position.current.context !== context) position.current = { context, top: 0 };
    scroll.current.scrollTop = position.current.top;
  });
  const selecting = tool === 'select' || (tool === 'artboard' && nodes.some(node => node.type === 'artboard'))
    || (nodes.length === 1 && ((tool === 'text' && nodes[0]?.type === 'text') || (tool === 'blend' && nodes[0]?.type === 'blend') || (tool === 'cloner' && ['cloner', 'effector'].includes(nodes[0]?.type ?? ''))));
  const multipleBoards = nodes.length > 1 && nodes.every(node => node.type === 'artboard');
  const artboardChosen = nodes.length === 1 && nodes[0]!.type === 'artboard' ? nodes[0]! : null;

  return (
    <aside
      aria-label="Properties"
      className={`properties-panel context-first pointer-events-auto absolute right-[8px] top-[60px] z-20 flex max-h-[calc(100%-120px)] flex-col overflow-hidden rounded-[18px] pointer-coarse:top-[68px] pointer-coarse:max-h-[calc(100%-136px)]`}
      style={{ width: PANEL_WIDTH }}
    >
      <header className="flex h-[48px] shrink-0 items-center gap-[8px] border-b border-separator pl-[14px] pr-[8px]">
        <h2 className="min-w-0 flex-1 truncate text-[13px] font-semibold text-label">
          {anchor && selecting ? `Point ${anchor.index + 1}` : !selecting ? rail?.title ?? toolLabel : nodes.length > 1 ? `${nodes.length} selected` : nodes[0]?.name || page.artboard?.name || 'Properties'}
        </h2>

        <IconButton icon="close" label="Close Properties" onClick={onClose} />
      </header>
      {(edit || info) && selecting ? <div className="shrink-0 border-b border-separator px-[14px] py-[8px]">
        <SegmentedControl size="small" label="Panel" className="w-full" value={shown} onChange={next => onTab?.(next)}
          segments={[{ value: 'properties' as const, label: 'Properties' }, ...(edit ? [{ value: 'edit' as const, label: 'Edit' }] : []), ...(info ? [{ value: 'info' as const, label: 'Info' }] : [])]} />
      </div> : null}
      <div ref={scroll} onScroll={event => { position.current = { context, top: event.currentTarget.scrollTop }; }} className="ui-scroll min-h-0 pb-[10px]" style={{ overflowAnchor: 'none' }}>
        {!selecting ? (
          // A tool's options are in the context bar over the canvas, once (audit, 15 Oct: the
          // strip and this panel drew Reshape's seven modes and two sliders twice). The panel
          // shows the page while a tool is in hand.
          <PageInspector {...page} showTitle={false} showNavigation={false} />
        ) : (
          <>
            {multipleBoards ? (
              <>
                <Section title="Artboards" id="context-artboards">
                  <MenuWell wide placement="below" well={{ icon: 'artboard', title: 'Size', value: 'Choose a Size', items: presetMenu(preset => actions.sizeBoards(nodes.map(node => node.id), preset.id)) }} />
                </Section>
                <PositionSection nodes={nodes} actions={actions} units={units} canAlign={canAlign} />
                <Section title="Background" id="context-background">
                  <PaintControl label="Fill" paint={nodes[0]?.fills?.[0]} onChange={paint => actions.updateAll(nodes.map(node => node.id), { fills: [paint] })} onClear={() => actions.updateAll(nodes.map(node => node.id), { fills: [] })} />
                </Section>
                <SelectionColorsOf actions={actions} />
              </>
            ) : artboardChosen ? (
              <>
                <LayerEffects key={artboardChosen.id} node={artboardChosen} actions={actions} units={units} after={<SelectionColorsOf actions={actions} />}>
                  <PositionSection nodes={nodes} actions={actions} units={units} canAlign={canAlign} />
                  <PageInspector {...page} artboard={artboardChosen} showTitle={false} showNavigation={false} />
                </LayerEffects>
              </>
            ) : nodes.length > 0 || anchor ? (
              // Keyed on what is chosen: a half-typed value used to commit to the NEXT layer
              // clicked, because the field outlived the layer it was typed for
              // (QA 2026-09-24, inspector-numbers-F5).
              shown !== 'properties' ? <div className="px-[14px] pb-[12px] pt-[10px]">{shown === 'edit' ? edit : info}</div>
              : <SelectionInspector key={nodes.map((node) => node.id).join(',')} nodes={nodes} units={units} actions={actions} canAlign={canAlign} fonts={fonts} anchor={anchor} repeat={repeat} variationRail={variationRail} blendRail={blendRail} />
            ) : (
              <PageInspector {...page} showTitle={false} showNavigation={false} />
            )}
          </>
        )}
      </div>
    </aside>
  );
}
